import { useState, useMemo } from "react";
import { IconAngleDown, IconAngleUp } from "@icons";
import { Role, ROLE_OPTIONS, ROLE_PERMISSIONS, Permission, ALL_PERMISSION_OPTIONS } from "../utils/permissions";
import { NestedOption, NestedOptionType, OptionGroup } from "../types";
import { toggleNestedRegionOption } from "../utils/regionDistrictOptions";
import "../css/UserEditModal.css";

interface UserEditModalProps {
    visible: boolean;
    isEditing: boolean;
    isSubmitting: boolean;
    formName: string;
    formEmail: string;
    selectedRole: Role | "";
    selectedPermissions: Permission[];
    selectedRegionOptions: NestedOption[];
    regionLocationOptions: OptionGroup[];
    onNameChange: (value: string) => void;
    onEmailChange: (value: string) => void;
    onRoleChange: (role: Role) => void;
    onPermissionsChange: (permissions: Permission[]) => void;
    onRegionOptionsChange: (options: NestedOption[]) => void;
    onSubmit: (e: { preventDefault: () => void }) => void;
    onCancel: () => void;
}

export const UserEditModal = ({
    visible,
    isEditing,
    isSubmitting,
    formName,
    formEmail,
    selectedRole,
    selectedPermissions,
    selectedRegionOptions,
    regionLocationOptions,
    onNameChange,
    onEmailChange,
    onRoleChange,
    onPermissionsChange,
    onRegionOptionsChange,
    onSubmit,
    onCancel,
}: UserEditModalProps) => {
    const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);

    // Permissions that come from the role (read-only, shown as included)
    const rolePermissions = useMemo(() => {
        if (!selectedRole) return [];
        return ROLE_PERMISSIONS[selectedRole as Role] || [];
    }, [selectedRole]);

    // Extra permissions toggled beyond the role defaults
    const extraPermissions = useMemo(() => {
        return selectedPermissions.filter((p) => !rolePermissions.includes(p));
    }, [selectedPermissions, rolePermissions]);

    const isPermissionFromRole = (perm: Permission) => rolePermissions.includes(perm);
    const isPermissionSelected = (perm: Permission) => selectedPermissions.includes(perm);

    const togglePermission = (perm: Permission) => {
        // Can't toggle off role-granted permissions
        if (isPermissionFromRole(perm)) return;
        if (isPermissionSelected(perm)) {
            onPermissionsChange(selectedPermissions.filter((p) => p !== perm));
        } else {
            onPermissionsChange([...selectedPermissions, perm]);
        }
    };

    // When role changes, set permissions to role defaults + any extras that were manually added
    const handleRoleChange = (role: Role) => {
        const newRolePerms = ROLE_PERMISSIONS[role] || [];
        // Keep any extra permissions that aren't in the new role
        const keptExtras = extraPermissions.filter((p) => !newRolePerms.includes(p));
        setRegionDropdownOpen(false);
        onRoleChange(role);
        onPermissionsChange([...newRolePerms, ...keptExtras]);
    };

    const handleRegionToggle = (option: NestedOption) => {
        onRegionOptionsChange(toggleNestedRegionOption(option, selectedRegionOptions, regionLocationOptions));
    };

    if (!visible) return null;

    const canSubmit = formName.trim() && formEmail.trim() && selectedRole;

    return (
        <div className="uem-overlay" onClick={onCancel}>
            <div className="uem-modal" onClick={(e) => e.stopPropagation()}>
                <div className="uem-header">
                    <h2>{isEditing ? `Edit ${formName}` : "Add User"}</h2>
                    <button className="uem-close" onClick={onCancel} aria-label="Close modal">×</button>
                </div>

                <div className="uem-body">
                    {/* Name & Email */}
                    <div className="uem-section">
                        <div className="uem-field-row">
                            <div className="uem-field">
                                <label htmlFor="uem-name">Name</label>
                                <input
                                    id="uem-name"
                                    type="text"
                                    value={formName}
                                    placeholder="e.g. Mark Stratton"
                                    onChange={(e) => onNameChange(e.target.value)}
                                />
                            </div>
                            {!isEditing && (
                                <div className="uem-field">
                                    <label htmlFor="uem-email">Email</label>
                                    <input
                                        id="uem-email"
                                        type="email"
                                        value={formEmail}
                                        placeholder="e.g. mark@company.com"
                                        onChange={(e) => onEmailChange(e.target.value)}
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <div className="uem-field">
                                    <label htmlFor="uem-email">Email</label>
                                    <input
                                        id="uem-email"
                                        type="email"
                                        value={formEmail}
                                        disabled
                                        style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Regions & Locations */}
                    <div className="uem-section">
                        <label className="uem-section-label">Regions & Locations</label>
                        <div className="uem-region-dropdown">
                            <button
                                type="button"
                                className="uem-region-trigger"
                                onClick={() => setRegionDropdownOpen(!regionDropdownOpen)}
                            >
                                <span>
                                    {selectedRegionOptions.length === 0
                                        ? "Select regions and locations"
                                        : selectedRegionOptions.some((o) => o.type === "all")
                                            ? "All Regions, Districts, and Locations"
                                            : `${selectedRegionOptions.length} selected`}
                                </span>
                                <span className="uem-chevron" aria-hidden="true">
                                    {regionDropdownOpen ? (
                                        <IconAngleUp iconClasses="uem-chevron-icon" />
                                    ) : (
                                        <IconAngleDown iconClasses="uem-chevron-icon" />
                                    )}
                                </span>
                            </button>
                            {regionDropdownOpen && (
                                <div className="uem-region-panel">
                                    {regionLocationOptions.map((group) => (
                                        <div key={group.label} className="uem-region-group">
                                            {group.options.map((opt) => {
                                                const indent = opt.type === "district" ? 1 : opt.type === "location" ? 2 : 0;
                                                const typePrefix = opt.type === "all" ? "Global Access: " : opt.type === "region" ? "Region: " : opt.type === "district" ? "District: " : opt.type === "location" ? "Location: " : "";
                                                return (
                                                    <label
                                                        key={opt.value}
                                                        className={`uem-region-option uem-indent-${indent}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                selectedRegionOptions.some((o) => o.type === "all") ||
                                                                selectedRegionOptions.some((o) => o.value === opt.value)
                                                            }
                                                            onChange={() =>
                                                                handleRegionToggle({
                                                                    label: opt.label,
                                                                    value: opt.value,
                                                                    type: opt.type as NestedOptionType,
                                                                })
                                                            }
                                                        />
                                                        <span className="uem-option-text">
                                                            <span className="uem-type-prefix">{typePrefix}</span>
                                                            {opt.label}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {selectedRegionOptions.length > 0 && (
                            <div className="uem-region-tags">
                                {selectedRegionOptions.map((opt) => (
                                    <span key={opt.value} className="uem-tag">
                                        {opt.label}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                onRegionOptionsChange(selectedRegionOptions.filter((o) => o.value !== opt.value))
                                            }
                                            aria-label={`Remove ${opt.label}`}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Role Selection */}
                    <div className="uem-section">
                        <label className="uem-section-label">Role</label>
                        <div className="uem-role-cards">
                            {ROLE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`uem-role-card ${selectedRole === opt.value ? "selected" : ""}`}
                                    onClick={() => handleRoleChange(opt.value as Role)}
                                >
                                    <span className="uem-role-name">{opt.label}</span>
                                    <span className="uem-role-count">
                                        {(ROLE_PERMISSIONS[opt.value as Role] || []).length} permissions
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Permissions */}
                    {selectedRole && (
                        <div className="uem-section">
                            <label className="uem-section-label">
                                Permissions
                                <span className="uem-section-hint">
                                    Checked items are included with the {ROLE_OPTIONS.find((r) => r.value === selectedRole)?.label} role. Toggle extras as needed.
                                </span>
                            </label>
                            <div className="uem-perm-groups">
                                {ALL_PERMISSION_OPTIONS.map((group) => (
                                    <div key={group.label} className="uem-perm-group">
                                        <div className="uem-perm-group-label">{group.label}</div>
                                        <div className="uem-perm-items">
                                            {group.options.map((opt) => {
                                                const fromRole = isPermissionFromRole(opt.value as Permission);
                                                const checked = isPermissionSelected(opt.value as Permission);
                                                return (
                                                    <label
                                                        key={opt.value}
                                                        className={`uem-perm-item ${fromRole ? "from-role" : ""} ${checked ? "checked" : ""}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            disabled={fromRole}
                                                            onChange={() => togglePermission(opt.value as Permission)}
                                                        />
                                                        <span className="uem-perm-label">{opt.label}</span>
                                                        {fromRole && <span className="uem-perm-badge">Role</span>}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>

                <div className="uem-footer">
                    <button type="button" className="uem-btn-cancel" onClick={onCancel}>Cancel</button>
                    <button
                        type="button"
                        className="uem-btn-submit"
                        disabled={!canSubmit || isSubmitting}
                        onClick={onSubmit}
                    >
                        {isSubmitting ? "Saving..." : isEditing ? "Save Changes" : "Add User"}
                    </button>
                </div>
            </div>
        </div>
    );
};
